type CoachInvalidationTag =
  | 'Clients'
  | 'Bookings'
  | 'Tasks'
  | 'Leads'
  | 'Funnel'
  | 'TrainingPlans'
  | 'Challenges'
  | 'Trainer'
  | 'Retention'
  | { type: 'Client'; id: string }
  | { type: 'ClientCoins'; id: string }
  | { type: 'ClientPackages'; id: string }
  | { type: 'Measurements'; id: string }
  | { type: 'TrainingPlan'; id: string }
  | { type: 'Challenges'; id: string };

let invalidateFn: ((tags: CoachInvalidationTag[]) => void) | null = null;

export function registerCacheInvalidator(fn: ((tags: CoachInvalidationTag[]) => void) | null): void {
  invalidateFn = fn;
}

export function invalidateQueryForUrl(
  url: string,
  params?: Record<string, unknown>,
  options?: { afterSync?: boolean },
): void {
  const tags = tagsForUrl(url, params, options);
  if (tags.length > 0) {
    invalidateFn?.(tags);
  }
}

function tagsForUrl(
  url: string,
  params?: Record<string, unknown>,
  options?: { afterSync?: boolean },
): CoachInvalidationTag[] {
  const path = url.split('?')[0] ?? url;

  if (path === '/clients') return ['Clients'];

  const clientMatch = path.match(/^\/clients\/([^/]+)$/);
  if (clientMatch) {
    if (options?.afterSync) {
      return [{ type: 'Client', id: clientMatch[1] }, 'Clients'];
    }
    return ['Clients'];
  }

  const clientCoinsMatch = path.match(/^\/clients\/([^/]+)\/coins$/);
  if (clientCoinsMatch) return [{ type: 'ClientCoins', id: clientCoinsMatch[1] }];

  const clientCoinsRedeemMatch = path.match(/^\/clients\/([^/]+)\/coins\/redeem$/);
  if (clientCoinsRedeemMatch) return [{ type: 'ClientCoins', id: clientCoinsRedeemMatch[1] }];

  const packagesMatch = path.match(/^\/clients\/([^/]+)\/packages$/);
  if (packagesMatch) return [{ type: 'ClientPackages', id: packagesMatch[1] }];

  const packageSessionMatch = path.match(
    /^\/clients\/([^/]+)\/packages\/([^/]+)\/(?:use-session|restore-session)$/,
  );
  if (packageSessionMatch) return [{ type: 'ClientPackages', id: packageSessionMatch[1] }];

  const packageItemMatch = path.match(/^\/clients\/([^/]+)\/packages\/([^/]+)$/);
  if (packageItemMatch) return [{ type: 'ClientPackages', id: packageItemMatch[1] }];

  if (path === '/bookings') return ['Bookings'];

  const bookingMatch = path.match(/^\/bookings\/([^/]+)$/);
  if (bookingMatch) return ['Bookings'];

  if (path === '/tasks') return ['Tasks'];

  const taskMatch = path.match(/^\/tasks\/([^/]+)/);
  if (taskMatch) return ['Tasks'];

  if (path === '/leads') return ['Leads', 'Funnel'];

  const leadMatch = path.match(/^\/leads\/([^/]+)/);
  if (leadMatch) return ['Leads', 'Funnel'];

  const measurementsMatch = path.match(/^\/measurements\/([^/]+)$/);
  if (measurementsMatch) {
    return [{ type: 'Measurements', id: measurementsMatch[1] }];
  }

  if (path === '/challenges') {
    const clientId = params?.clientId as string | undefined;
    return clientId ? [{ type: 'Challenges', id: clientId }] : ['Challenges'];
  }

  const challengeMatch = path.match(/^\/challenges\/([^/]+)$/);
  if (challengeMatch) return ['Challenges'];

  if (path === '/training-plans') return ['TrainingPlans'];

  const planMatch = path.match(/^\/training-plans\/([^/]+)$/);
  if (planMatch) return [{ type: 'TrainingPlan', id: planMatch[1] }];

  if (path === '/trainers/me') return ['Trainer'];

  if (path === '/analytics/retention') return ['Retention'];

  if (path === '/analytics/funnel') return ['Funnel'];

  return [];
}
